/**
 * Fait : décrit les documents des collections `catalogueDeModeles`, `budgets`
 *   et `reservationsDUsage` — quels modèles d'IA existent, sous quel plafond,
 *   et ce que chaque génération réserve puis consomme. Les deux natures
 *   d'usage — euros pour une clé API, appels et jetons pour un abonnement —
 *   sont des variantes distinctes qui ne s'additionnent jamais (D-015).
 *
 * Ne fait pas : il ne réserve rien et ne compte rien — la réservation atomique
 *   en transaction est le métier de la phase 5 — et ne porte aucun prix ni
 *   identifiant de modèle en dur : le catalogue est LA source, administrable
 *   et datée.
 */

import { z } from 'zod';

import { FOURNISSEURS } from '../schemas-catalogue.ts';
import { identifiant, nomDonne, versionDeRessource } from '../schemas.ts';

// La liste blanche des fournisseurs et les voies vivent dans le contrat
// PUBLIC (`schemas-catalogue.ts`) : le formulaire d'administration du client
// en a besoin autant que ce modèle. Réexportées ici pour ses consommateurs.
export {
  FOURNISSEURS,
  VOIES_D_AUTHENTIFICATION,
  type Fournisseur,
  type VoieDAuthentification,
} from '../schemas-catalogue.ts';

/** Champs communs aux deux voies du catalogue. */
const socleDEntreeDeCatalogue = {
  fournisseur: z.enum(FOURNISSEURS),

  /**
   * L'identifiant exact chez le fournisseur. C'est une chaîne libre, jamais
   * une énumération : le catalogue est administrable, le code ne connaît
   * aucun nom de modèle (« aucun identifiant codé en dur hors du catalogue »).
   */
  identifiantModele: z.string().min(1),

  libelle: nomDonne,
  actif: z.boolean(),

  /** Date du relevé des conditions — le catalogue est « administrable et daté ». */
  releveLe: z.date(),

  version: versionDeRessource,
  creeLe: z.date(),
  misAJourLe: z.date(),
};

/**
 * Une entrée du catalogue. La voie « clé API » porte des prix ; la voie
 * « abonnement » n'en porte AUCUN — son coût marginal est nul, et lui
 * inventer un prix laisserait croire qu'on peut l'additionner aux euros.
 */
export const entreeDeCatalogue = z.discriminatedUnion('voie', [
  z
    .object({
      voie: z.literal('cle_api'),

      /**
       * Prix en CENTIMES par MILLION de jetons, entiers : l'argent ne se
       * compte jamais en flottants, une erreur d'arrondi est une erreur de
       * facturation.
       */
      prixEntreeCentimesParMillionDeJetons: z.number().int().min(0),
      prixSortieCentimesParMillionDeJetons: z.number().int().min(0),

      ...socleDEntreeDeCatalogue,
    })
    .strict(),
  z.object({ voie: z.literal('abonnement'), ...socleDEntreeDeCatalogue }).strict(),
]);

export type EntreeDeCatalogue = z.infer<typeof entreeDeCatalogue>;

/**
 * Un plafond de dépense, global ou par membre. Zéro signifie FERMÉ, et le
 * plafond global à zéro bloque TOUTES les voies, abonnement compris — sans
 * cette règle, la voie sans coût marginal contournerait le garde-fou (D-006).
 */
/**
 * Les compteurs matérialisés du budget — ajoutés au lot 5.4. La réservation
 * est un `$inc` CONDITIONNEL sur ce document : deux transactions concurrentes
 * qui liraient l'encours puis inséreraient chacune passeraient toutes les
 * deux (write skew) ; le compteur sur UN document force l'arbitrage par la
 * base (critère 27). `engage` porte les réservations en cours, `consomme` le
 * régularisé cumulé — dépensé = engagé + consommé.
 */
const compteursDeBudget = {
  engageCentimes: z.number().int().min(0).default(0),
  consommeCentimes: z.number().int().min(0).default(0),
};

export const budget = z.discriminatedUnion('portee', [
  z
    .object({
      portee: z.literal('global'),
      plafondCentimes: z.number().int().min(0),
      ...compteursDeBudget,
      version: versionDeRessource,
      misAJourLe: z.date(),
    })
    .strict(),
  z
    .object({
      portee: z.literal('membre'),
      compteId: identifiant,
      plafondCentimes: z.number().int().min(0),
      ...compteursDeBudget,
      version: versionDeRessource,
      misAJourLe: z.date(),
    })
    .strict(),
]);

export type Budget = z.infer<typeof budget>;

/** Vie d'une réservation : réservée, puis régularisée par l'usage réel, puis
 * réconciliée si la génération a été interrompue — ou annulée. */
export const ETATS_DE_RESERVATION = ['reservee', 'regularisee', 'reconciliee', 'annulee'] as const;

export type EtatDeReservation = (typeof ETATS_DE_RESERVATION)[number];

/** Champs communs aux deux natures de réservation. */
const socleDeReservation = {
  generationId: identifiant,
  projetId: identifiant,

  /** Le membre qui a déclenché la génération — l'UNIQUE payeur (D-013). */
  payeurId: identifiant,

  fournisseur: z.enum(FOURNISSEURS),
  identifiantModele: z.string().min(1),
  etat: z.enum(ETATS_DE_RESERVATION),

  creeLe: z.date(),
  misAJourLe: z.date(),
};

/**
 * Ce qu'une génération réserve avant tout contact réseau. La nature « euros »
 * compte des centimes ; la nature « quota d'abonnement » compte des appels et
 * des jetons contre le quota du fournisseur. Le tableau familial affiche les
 * deux natures côte à côte, jamais additionnées.
 */
export const reservationDUsage = z.discriminatedUnion('nature', [
  z
    .object({
      nature: z.literal('euros'),
      estimationCentimes: z.number().int().min(0),

      /** Posé à la régularisation, quand l'usage réel est connu. */
      coutReelCentimes: z.number().int().min(0).optional(),

      ...socleDeReservation,
    })
    .strict(),
  z
    .object({
      nature: z.literal('quota_abonnement'),
      appelsComptes: z.number().int().min(1),

      /** Posé à la régularisation — le fournisseur annonce les jetons servis. */
      jetonsComptes: z.number().int().min(0).optional(),

      ...socleDeReservation,
    })
    .strict(),
]);

export type ReservationDUsage = z.infer<typeof reservationDUsage>;
